export type FeaturedCompare = {
  aId: string;
  bId: string;
  slug: string;
  title: string;
  summary: string;
  bodyHtml: string;
};

/**
 * Written featured comparisons (not link stubs).
 * slug must match `${a.slug}-vs-${b.slug}` from catalog products.
 */
export const FEATURED_COMPARES: FeaturedCompare[] = [
  {
    aId: 'p-bud-light',
    bId: 'p-dogfish-90',
    slug: 'bud-light-vs-dogfish-head-90-minute-ipa',
    title: 'Bud Light vs Dogfish Head 90 Minute IPA',
    summary:
      'A light lager versus a strong IPA — match alcohol before you judge calories or value.',
    bodyHtml: `
<p>Bud Light sits in the classic American light-lager band; Dogfish Head 90 Minute is a strong IPA with substantially more ABV per ounce. Comparing one can to one can overstates how “heavy” the IPA is in social terms and understates how much ethanol it delivers.</p>
<p>Use Compare to align pure alcohol with servings, then read calories for that matched amount and alcohol per 100 calories on each row. Package alcohol per dollar depends on the multipack you buy — override prices to match your store.</p>
<p>This is a composition exercise, not a taste verdict. For adults of legal drinking age. Drink responsibly.</p>
<p>Read more: <a href="/guides/light-beer-vs-double-ipa/">Light beer vs double IPA</a>.</p>`,
  },
  {
    aId: 'p-vodka-soda',
    bId: 'p-vodka-tonic',
    slug: 'vodka-soda-vs-vodka-tonic',
    title: 'Vodka soda vs vodka tonic',
    summary:
      'Same spirit pour, different mixers — calories move, alcohol mostly does not.',
    bodyHtml: `
<p>These catalog rows are home recipe estimates. Hold the vodka pour fixed and swap soda for tonic: ethanol stays similar while sugar in tonic raises calories and lowers alcohol per 100 calories.</p>
<p>Recipe prices are prorated home costs, not bar tabs. Open the interactive compare to adjust servings and see both ratio columns.</p>
<p>Guide: <a href="/guides/vodka-soda-vs-vodka-tonic/">Vodka soda vs vodka tonic</a>.</p>`,
  },
  {
    aId: 'p-michelob-ultra',
    bId: 'p-bud-light',
    slug: 'michelob-ultra-vs-bud-light',
    title: 'Michelob Ultra vs Bud Light',
    summary:
      'Two mainstream light lagers — close on marketing, worth checking on ABV, calories, and pack price.',
    bodyHtml: `
<p>Ultra and Bud Light compete in the same shopping aisle. Small differences in ABV and calories change alcohol per 100 calories; multipack prices change alcohol per dollar. Neither ranking is a health grade.</p>
<p>Set one as a baseline in the beer catalog to find nearby lights that beat it on both calories and cost per standard drink.</p>`,
  },
  {
    aId: 'p-garage-beer',
    bId: 'p-michelob-ultra',
    slug: 'garage-beer-classic-vs-michelob-ultra',
    title: 'Garage Beer Classic vs Michelob Ultra',
    summary:
      'Two ~95-calorie light lagers with transparent producer panels — a clean composition demo.',
    bodyHtml: `
<p>Garage Beer Classic lists about 4.0% ABV, 95 calories, and 3g carbs per 12 fl oz. Michelob Ultra lists about 4.2% ABV, 95 calories, and 2.6g carbs in the catalog snapshot. With equal calories, the slightly higher ABV edges alcohol per 100 calories.</p>
<p>Pack value depends on 12-pack versus larger multipack pricing — edit local prices before trusting alcohol per dollar.</p>
<p>Full write-up: <a href="/guides/garage-beer-vs-michelob-ultra/">Garage Beer vs Michelob Ultra</a>.</p>`,
  },
  {
    aId: 'p-miller-lite',
    bId: 'p-coors-light',
    slug: 'miller-lite-vs-coors-light',
    title: 'Miller Lite vs Coors Light',
    summary:
      'Peer light lagers — use verified panels and your pack price, not slogans.',
    bodyHtml: `
<p>Miller Lite and Coors Light are frequently compared in advertising. On BoozeRatio, compare ABV, calories, and the package you actually buy. Alcohol per 100 calories answers composition; alcohol per dollar answers the cart.</p>
<p>Override prices on Compare if your grocery tag differs from the illustrative offer.</p>`,
  },
  {
    aId: 'p-garage-beer',
    bId: 'p-garage-beer-lime',
    slug: 'garage-beer-classic-vs-garage-beer-lime',
    title: 'Garage Beer Classic vs Garage Beer Lime',
    summary:
      'Same nutrition panel on the producer site — choose on flavor, rank on packs.',
    bodyHtml: `
<p>Producer materials list Classic and Lime at 4% ABV, 95 calories, and 3g carbs per 12 fl oz. Composition rankings should look alike; package offers and local prices still matter for alcohol per dollar.</p>
<p>Use Compare if you want side-by-side offers, or pick based on taste preference.</p>`,
  },
  {
    aId: 'p-dogfish-slightly-mighty',
    bId: 'p-dogfish-60',
    slug: 'dogfish-head-slightly-mighty-vs-dogfish-head-60-minute-ipa',
    title: 'Dogfish Head Slightly Mighty vs 60 Minute IPA',
    summary:
      'A lighter Dogfish option versus a classic mid-strength IPA from the same brand family.',
    bodyHtml: `
<p>Within one brand family you can still see large ABV and calorie gaps. Slightly Mighty is positioned as a lighter IPA experience; 60 Minute sits in a fuller craft IPA band. Match alcohol with servings before calling either “better.”</p>
<p>Related guide: <a href="/guides/lower-calorie-ipa-alternatives/">Lower-calorie IPA alternatives</a>.</p>`,
  },
  {
    aId: 'p-michelob-ultra',
    bId: 'p-miller-lite',
    slug: 'michelob-ultra-vs-miller-lite',
    title: 'Michelob Ultra vs Miller Lite',
    summary:
      'Another light-lager peer set — check the live catalog numbers and your multipack.',
    bodyHtml: `
<p>Ultra and Miller Lite both market lean profiles. Small calorie and ABV differences show up in alcohol per 100 calories; club-size packs change alcohol per dollar. Rankings are informational for adults of legal drinking age.</p>`,
  },
];

export function getFeaturedCompare(pairSlug: string): FeaturedCompare | undefined {
  return FEATURED_COMPARES.find((c) => c.slug === pairSlug);
}
